/**
 * GlassPanel — reusable glassmorphism surface.
 *
 * Renders a `<div>` (or any tag via `as`) with backdrop-blur, semi-transparent
 * background, and the standard glass border + shadow from design tokens.
 *
 * Usage:
 *   <GlassPanel>…content…</GlassPanel>
 *   <GlassPanel className="my-panel" style={{ padding: 24 }}>…</GlassPanel>
 *   <GlassPanel glow="accent">…</GlassPanel>  // adds accent neon glow
 */

import type { CSSProperties, ElementType, ReactNode } from 'react'

export type GlassPanelGlow = 'none' | 'accent' | 'success' | 'warning' | 'danger'

export interface GlassPanelProps {
  children?: ReactNode
  className?: string
  style?: CSSProperties
  glow?: GlassPanelGlow
  /** HTML element / React component to render as. Defaults to 'div'. */
  as?: ElementType
  onClick?: () => void
  role?: string
  'aria-label'?: string
}

const BASE_CLASSES = 'bg-[rgba(11,18,32,0.72)] backdrop-blur-md border border-[rgba(255,255,255,0.08)] rounded-2xl shadow-glass'

const GLOW_BORDER: Record<GlassPanelGlow, string> = {
  none:    'rgba(255,255,255,0.08)',
  accent:  'rgba(6,182,212,0.25)',
  success: 'rgba(34,197,94,0.22)',
  warning: 'rgba(245,158,11,0.22)',
  danger:  'rgba(239,68,68,0.22)',
}

const GLOW_SHADOW: Record<GlassPanelGlow, string> = {
  none:    '0 4px 32px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.04) inset',
  accent:  '0 4px 32px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.04) inset, 0 0 20px rgba(6,182,212,0.40)',
  success: '0 4px 32px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.04) inset, 0 0 12px rgba(34,197,94,0.35)',
  warning: '0 4px 32px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.04) inset, 0 0 12px rgba(245,158,11,0.35)',
  danger:  '0 4px 32px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.04) inset, 0 0 12px rgba(239,68,68,0.35)',
}

export function GlassPanel({
  children,
  className = '',
  style,
  glow = 'none',
  as: Tag = 'div',
  ...rest
}: GlassPanelProps) {
  return (
    <Tag
      className={`${BASE_CLASSES} ${className}`.trim()}
      style={{ borderColor: GLOW_BORDER[glow], boxShadow: GLOW_SHADOW[glow], ...style }}
      {...rest}
    >
      {children}
    </Tag>
  )
}
